import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CalendarCheck, Flag, Briefcase, ClipboardList, Target,
  MessageSquareText, Users, ChevronRight, ShieldCheck,
} from 'lucide-react';
import Button from '../../../components/ui/Button';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// OBF-01 — kırmızı "Risk: Yüksek" rozeti KALDIRILDI: şirketin erken-ayrılma
// değerlendirmesi değerlendirmenin öznesine gösterilmez. Kart artık her zaman
// nötr; risk renklendirmesi ($risk) ile birlikte gitti.
const Hero = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.main};
  border-radius: 12px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const HeroTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeroLabel = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Countdown = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const CountNumber = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 40px;
  line-height: 1;
  font-weight: ${({ theme }) => theme.metrics.font.weight.extraBold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const CountLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const ProgressWrap = styled.div`
  margin-top: 18px;
`;

const ProgressHead = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 6px;
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgColor};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.secondary.main};
  transition: width 0.3s ease;
`;

const MetaRow = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  }
`;

const NpsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const NpsCard = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 14px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const NpsDay = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const NpsValue = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 2px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
`;

const StatusCard = styled.button`
  text-align: left;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: box-shadow 0.15s, transform 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardMeta = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
`;

// Manager-approved checkpoint awaiting the employee's co-sign. Prominent so the
// new hire actually completes the dual sign-off — the period won't advance / the
// onboarding won't close until they acknowledge (or HR overrides).
const AckBanner = styled.div`
  background: rgba(2,145,239,0.06);
  border: 1px solid #BFD9F2;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.main};
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const AckIcon = styled.div`
  color: ${({ theme }) => theme.colors.primary.main};
  flex-shrink: 0;
`;

const AckBody = styled.div`
  flex: 1;
  min-width: 200px;
`;

const AckTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AckText = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
`;

const AckNote = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: #fff;
  border: 1px solid #E1EAF2;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 8px;
  font-style: italic;
  white-space: pre-wrap;
`;

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(lang || undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// NotesMarkdown biriken bir kayıttır (istek notu + "[approve] ..." + override).
// Çalışana teyit ekranında yöneticinin onay değerlendirmesini göster: son
// "[approve] " satırının içeriği.
const extractApproveNote = (notes) => {
  if (!notes) return null;
  const lines = String(notes).split('\n').filter((l) => l.startsWith('[approve] '));
  if (lines.length === 0) return null;
  const note = lines[lines.length - 1].slice('[approve] '.length).trim();
  return note || null;
};

export default function OverviewTab({ home, onGoTab, pendingAcks = [], onAcknowledge }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const days = home.daysUntilNextCheckpoint;
  const nps = [
    { day: 30, v: home.day30NpsScore },
    { day: 60, v: home.day60NpsScore },
    { day: 90, v: home.day90NpsScore },
  ];

  return (
    <Grid>
      {pendingAcks.map((pa) => {
        const managerNote = extractApproveNote(pa.notesMarkdown);
        return (
          <AckBanner key={pa.id}>
            <AckIcon><ShieldCheck size={26} /></AckIcon>
            <AckBody>
              <AckTitle>{t('myOnboarding.periodAck.title')}</AckTitle>
              <AckText>
                {t('myOnboarding.periodAck.text', {
                  period: t(`onboardingOneOnOne.period.${pa.period}`, { defaultValue: pa.period }),
                })}
                {pa.approvedAt ? ` (${fmtDate(pa.approvedAt, lang)})` : ''}
              </AckText>
              {managerNote && (
                <AckNote>{t('myOnboarding.periodAck.managerNote')}: {managerNote}</AckNote>
              )}
            </AckBody>
            <Button onClick={() => onAcknowledge && onAcknowledge(pa.id)}>
              {t('myOnboarding.periodAck.button')}
            </Button>
          </AckBanner>
        );
      })}

      <Hero>
        <HeroTop>
          <HeroLabel>{t('myOnboarding.overview.heroTitle')}</HeroLabel>
        </HeroTop>

        <Countdown>
          {days != null && days > 0 ? (
            <>
              <CountNumber>{days}</CountNumber>
              <CountLabel>
                {t('myOnboarding.overview.daysUntilCheckpoint', { checkpoint: home.nextCheckpointLabel || '' })}
              </CountLabel>
            </>
          ) : (
            <CountLabel>{t('myOnboarding.overview.allCheckpointsPassed')}</CountLabel>
          )}
        </Countdown>

        <ProgressWrap>
          <ProgressHead>
            <span>{t('myOnboarding.overview.progress')}</span>
            {/* OBB-05b - sunucu artik olcecek zorunlu gorev yokken null doner
                (eskiden 100); "%100 tamamlandi" ile "henuz gorev yok" ayrilir. */}
            <span>
              {home.overallProgressPct != null
                ? `%${home.overallProgressPct}`
                : t('myOnboarding.overview.noTasksYet')}
            </span>
          </ProgressHead>
          <ProgressTrack>
            <ProgressFill $pct={home.overallProgressPct ?? 0} />
          </ProgressTrack>
        </ProgressWrap>

        <MetaRow>
          <MetaItem>
            <CalendarCheck size={16} />
            {t('myOnboarding.overview.startDate')}:&nbsp;<strong>{fmtDate(home.startDate, lang)}</strong>
          </MetaItem>
          <MetaItem>
            <Flag size={16} />
            {t('myOnboarding.overview.currentPeriod')}:&nbsp;
            <strong>{t(`onboardingOneOnOne.period.${home.currentPeriod}`, { defaultValue: home.currentPeriod || '—' })}</strong>
          </MetaItem>
          <MetaItem>
            <Briefcase size={16} />
            {t('myOnboarding.overview.source')}:&nbsp;
            <strong>{t(`myOnboarding.overview.sourceType.${home.source}`, { defaultValue: home.source })}</strong>
          </MetaItem>
        </MetaRow>
      </Hero>

      <NpsRow>
        {nps.map((n) => (
          <NpsCard key={n.day}>
            <NpsDay>{t('myOnboarding.overview.npsDay', { day: n.day })}</NpsDay>
            <NpsValue>{n.v != null ? n.v : '—'}</NpsValue>
          </NpsCard>
        ))}
      </NpsRow>

      <CardsGrid>
        <StatusCard onClick={() => onGoTab('tasks')}>
          <CardIcon $bg="rgba(2,145,239,0.10)" $color="#0291EF"><ClipboardList size={20} /></CardIcon>
          <CardBody>
            <CardTitle>{t('myOnboarding.tab.tasks')}</CardTitle>
            <CardMeta>
              {t('myOnboarding.overview.mandatoryTasks', { done: home.mandatoryTaskCompleted, total: home.mandatoryTaskTotal })}
            </CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>

        <StatusCard onClick={() => onGoTab('goals')}>
          <CardIcon $bg="rgba(2,211,191,0.12)" $color="#01A899"><Target size={20} /></CardIcon>
          <CardBody>
            <CardTitle>{t('myOnboarding.tab.goals')}</CardTitle>
            <CardMeta>{t('myOnboarding.overview.goalsHint')}</CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>

        <StatusCard onClick={() => onGoTab('checkins')}>
          <CardIcon $bg="rgba(255,193,7,0.14)" $color="#C79100"><MessageSquareText size={20} /></CardIcon>
          <CardBody>
            <CardTitle>{t('myOnboarding.tab.checkins')}</CardTitle>
            <CardMeta>{t('myOnboarding.overview.checkinsHint')}</CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>

        <StatusCard onClick={() => onGoTab('oneOnOnes')}>
          <CardIcon $bg="rgba(124,58,237,0.10)" $color="#7C3AED"><Users size={20} /></CardIcon>
          <CardBody>
            <CardTitle>{t('myOnboarding.tab.oneOnOnes')}</CardTitle>
            <CardMeta>{t('myOnboarding.overview.oneOnOnesHint')}</CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>
      </CardsGrid>
    </Grid>
  );
}
